import {
  DEFAULT_PARTY_TIME_NORMAL_CURVE,
  DEFAULT_SOL_PARTY_LIMIT_AGENTS,
  DEFAULT_SOL_PARTY_LIMIT_IDO,
  DEFAULT_TOKEN_IDO_CURVE_MIN,
} from '@/config';
import useOnClickOutside from '@/hooks/useOnClickOutside';
import { Web3SolanaProgramInteraction } from '@/program/web3';
import { Web3DefaiProgramInteraction } from '@/program/web3DefaiLaunchpad';
import { uploadMetadata } from '@/utils/fileUpload';
import { CreateCoinInfo, LaunchDataInfo, MetadataInfo } from '@/utils/types';
import {
  BACKEND_URL,
  genTokenKeypair,
  getCoinsInfoBy,
  getUserByWalletAddress,
} from '@/utils/util';
import { CoinType } from '@/zustand-store/coin/useCoinStore';
import { useGetConfigState } from '@/zustand-store/config/selector';
import { useWallet } from '@solana/wallet-adapter-react';
import { Keypair } from '@solana/web3.js';
import base58 from 'bs58';
import { useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { EditIcon } from '../icons';
import { errorAlert } from '../others/ToastGroup';
import { FUND_EXPIRATION_LIST, LaunchingMode } from './config';
import ConfirmModal from './Confirm';
import CustomInfoCurve from './CustomInfoCurve';
import LoadingCreateCoin from './LoadingCreateCoin';

// const coin = FAKE_UPCOMING_TOKEN;
const SetupInvestmentStrategy = ({ coin, onCreateSuccess }) => {
  const isDevnet = ['devnet', 'mainnet-beta-test'].includes(
    import.meta.env.VITE_APP_SOLANA_ENV,
  );
  const curveAgentConfig = useGetConfigState('bondingCurveConfig');
  const [isLoadingConfirm, setIsLoadingConfirm] = useState(false);
  const [mintKp, setMintKp] = useState<Keypair>();
  const [showConfirm, setShowConfirm] = useState<boolean>(false);
  const curveLimitAgentNumber =
    (curveAgentConfig.curveLimit?.toNumber() || 0) / 1e7;
  const [fundExp, setFundExp] = useState(FUND_EXPIRATION_LIST[0]);
  const [curve, setCurve] = useState(curveLimitAgentNumber);
  const [partyTime, setPartyTime] = useState(DEFAULT_PARTY_TIME_NORMAL_CURVE);
  const [partyLimitAmount, setPartyLimitAmount] = useState(
    DEFAULT_SOL_PARTY_LIMIT_AGENTS,
  );
  const [mode, setMode] = useState(LaunchingMode.FAIR);
  const wallet = useWallet();
  const [errors, setErrors] = useState({
    curve: '',
    partyRoundTime: '',
    interval: '',
  });

  const [showCustom, setShowCustom] = useState<boolean>(false);
  const [dataCoin, setDataCoin] = useState(coin);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setDataCoin(coin);
  }, [coin]);

  const fetchCreatedCoin = async (userAddress: string) => {
    try {
      const response = await getUserByWalletAddress({
        baseUrl: BACKEND_URL,
        wallet: userAddress,
      });
      const userId = response?._id;

      if (!userId) return;
      const coinsBy = await getCoinsInfoBy(BACKEND_URL, userId);
      if (coinsBy) {
        // setUnCreatableToken(coinsBy);
        return coinsBy;
      }
    } catch (error) {
      console.log('Error fetching coins:', error);
    }
  };

  const isIdo = mode === LaunchingMode.IDO;

  const onLaunch = async () => {
    try {
      setIsLoadingConfirm(true);

      const envMode = import.meta.env.VITE_APP_SOLANA_ENV;
      let mintKp = Keypair.generate();
      if (envMode === 'mainnet-beta') {
        console.log('gen Orai prefix');
        const key = await genTokenKeypair(BACKEND_URL);
        mintKp = Keypair.fromSecretKey(base58.decode(key));
      }
      console.log('tokenAddress:', mintKp.publicKey.toBase58());

      setMintKp(mintKp);
      setShowConfirm(true);
    } catch (error) {
      console.log('error gen token address', error);
      errorAlert('Failed to gen token address. Try again!');
    } finally {
      setIsLoadingConfirm(false);
    }
  };
  const createCoin = async () => {
    // if (!validateFormToken()) {
    //   errorAlert('Please fix the errors before submitting.');
    //   return;
    // }

    try {
      setIsLoading(true);

      const unCreatableToken = await fetchCreatedCoin(
        wallet.publicKey?.toBase58(),
      );

      if (unCreatableToken) {
        const isSelectedAgentHasCoin = unCreatableToken.find(
          (tk) => tk.metadata?.agentAddress === coin.metadata?.agentAddress,
        );
        if (isSelectedAgentHasCoin && !isDevnet) {
          errorAlert(
            'Your agent has been tokenized! Please select another agent!',
          );
          return;
        }
      }

      // const defaiMode = partyLimitTab;
      const isIdo = mode === LaunchingMode.IDO;
      const jsonData: MetadataInfo = {
        name: coin?.name,
        symbol: coin?.ticker,
        image: coin.url,
        description: coin?.description,
        createdOn: 'https://agents.land',
        twitter: coin?.metadata?.twitter || undefined, // Only assign if it exists
        website: coin?.metadata?.website || undefined, // Only assign if it exists
        telegram: coin?.metadata?.telegram || undefined, // Only assign if it exists
        discord: coin?.metadata?.discord || undefined, // Only assign if it exists
        agentId: coin?.metadata?.agentId,
        agentClanId: coin?.metadata?.agentClanId || '', //
        agentAddress: coin?.metadata?.agentAddress,
        creatorAddress: wallet.publicKey?.toBase58(),
        createdAt: Date.now(),
        tags: mode === LaunchingMode.IDO ? CoinType.DEFAI : CoinType.AGENT,
        mode: null,
        isIdoLaunch: isIdo,
        upcomingCoinId: coin._id,
      };
      // Process metadata upload
      const uploadMetadataUrl = await uploadMetadata(jsonData);
      if (!uploadMetadataUrl) {
        errorAlert('Metadata upload failed.');
        setIsLoading(false);
        return;
      }
      console.log('uploadMetadataUrl', coin.url, uploadMetadataUrl);
      const coinData: LaunchDataInfo = {
        name: coin?.name,
        symbol: coin?.ticker,
        uri: uploadMetadataUrl,
        presale: coin?.presale,
        metadata: jsonData,
        decimals: 6,
        curveLimit: curve,
        whitelistRoundPeriod: partyTime,
        fundsExpiration: fundExp.value,
        partyLimit: isIdo
          ? DEFAULT_SOL_PARTY_LIMIT_IDO
          : DEFAULT_SOL_PARTY_LIMIT_AGENTS,
        isIdoLaunch: isIdo,
      };
      console.log('coinData--->', coinData);

      const web3Solana = isIdo
        ? new Web3DefaiProgramInteraction()
        : new Web3SolanaProgramInteraction();
      const res = await web3Solana.createToken(wallet, coinData, mintKp);

      if (res === 'WalletError' || !res) {
        errorAlert('Payment failed or was rejected.');
        setIsLoading(false);
        return;
      }

      onCreateSuccess({
        ...coinData,
        token: mintKp?.publicKey?.toBase58(),
      });
    } catch (error) {
      errorAlert('An unexpected error occurred.');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full">
      <ConfirmModal
        isOpen={showConfirm}
        coin={{
          name: coin.name,
          symbol: coin.ticker,
          url: coin.url,
          token: mintKp?.publicKey?.toBase58(),
        }}
        closeModal={() => setShowConfirm(false)}
        onConfirm={async () => {
          createCoin();
          setShowConfirm(false);
        }}
      />
      <CustomInfoCurve
        isOpen={showCustom}
        coin={dataCoin}
        closeModal={() => setShowCustom(false)}
        onConfirm={(data) => {
          // update data coin
          console.log('data', data);
          setShowCustom(false);
        }}
        fundExp={fundExp}
        setFundExp={setFundExp}
        curve={curve}
        setCurve={setCurve}
        party={partyTime}
        setParty={setPartyTime}
      ></CustomInfoCurve>

      {isLoading && (
        <LoadingCreateCoin isLoading={isLoading} setIsLoading={setIsLoading} />
      )}

      <div className="bg-[#13141D] rounded-lg border border-[#1A1C28] p-4 md:p-8 flex flex-col w-full flex-wrap mb-8">
        <div className="flex w-full md:flex-nowrap flex-wrap bg-[#080A14] rounded p-1 md:p-2 mb-4">
          <div
            className={twMerge(
              'flex items-center justify-center flex-1 text-center uppercase group relative cursor-pointer mr-1 md:mr-4 px-1 md:px-4 py-[6px] text-[10px] md:text-[14px] rounded text-[#9192A0]',
              mode === LaunchingMode.FAIR &&
                'bg-[rgba(228,_119,_93,_0.24)] text-[#E4775D]',
            )}
            onClick={() => {
              setMode(LaunchingMode.FAIR);
              setCurve(curveLimitAgentNumber);
              setPartyTime(DEFAULT_PARTY_TIME_NORMAL_CURVE);
              setPartyLimitAmount(DEFAULT_SOL_PARTY_LIMIT_AGENTS);
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="21"
              height="20"
              viewBox="0 0 21 20"
              fill="none"
            >
              <path
                d="M17.1663 11.6666C17.1663 15.3485 14.1816 18.3333 10.4997 18.3333C6.81778 18.3333 3.83301 15.3485 3.83301 11.6666C3.83301 10.7825 4.0051 9.93865 4.31761 9.16663C5.30658 6.72343 10.4997 1.66663 10.4997 1.66663C10.4997 1.66663 15.6928 6.72343 16.6817 9.16663C16.9943 9.93865 17.1663 10.7825 17.1663 11.6666Z"
                stroke={mode === LaunchingMode.FAIR ? '#E4775D' : '#9192A0'}
                strokeWidth="1.66667"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            &nbsp;Fair Mode
          </div>
          <div
            className={twMerge(
              'flex items-center justify-center flex-1 text-center group relative uppercase cursor-pointer px-1 md:px-4 py-[6px] text-[10px] md:text-[14px] rounded text-[#9192A0]',
              mode === LaunchingMode.IDO &&
                'bg-[rgba(228,_119,_93,_0.24)] text-[#E4775D]',
            )}
            onClick={() => {
              setMode(LaunchingMode.IDO);
              setCurve(DEFAULT_TOKEN_IDO_CURVE_MIN);
              setPartyTime(DEFAULT_PARTY_TIME_NORMAL_CURVE);
              setPartyLimitAmount(DEFAULT_SOL_PARTY_LIMIT_IDO);
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="21"
              height="20"
              viewBox="0 0 21 20"
              fill="none"
            >
              <path
                d="M10.5003 14.1667C10.5003 16.4679 12.3658 18.3333 14.667 18.3333C16.9682 18.3333 18.8337 16.4679 18.8337 14.1667C18.8337 11.8655 16.9682 10 14.667 10C12.3658 10 10.5003 11.8655 10.5003 14.1667ZM10.5003 14.1667C10.5003 13.2285 10.8104 12.3627 11.3337 11.6663V4.16667M10.5003 14.1667C10.5003 14.8545 10.667 15.5033 10.9621 16.0751C10.2601 16.6681 8.63853 17.0833 6.75033 17.0833C4.21902 17.0833 2.16699 16.3371 2.16699 15.4167V4.16667M11.3337 4.16667C11.3337 5.08714 9.28163 5.83333 6.75033 5.83333C4.21902 5.83333 2.16699 5.08714 2.16699 4.16667M11.3337 4.16667C11.3337 3.24619 9.28163 2.5 6.75033 2.5C4.21902 2.5 2.16699 3.24619 2.16699 4.16667M2.16699 11.6667C2.16699 12.5871 4.21902 13.3333 6.75033 13.3333C8.57449 13.3333 10.1498 12.9458 10.8875 12.3848M11.3337 7.91667C11.3337 8.83714 9.28163 9.58333 6.75033 9.58333C4.21902 9.58333 2.16699 8.83714 2.16699 7.91667"
                stroke={mode === LaunchingMode.IDO ? '#E4775D' : '#9192A0'}
                strokeWidth="1.66667"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            &nbsp;IDO Mode
          </div>
        </div>
        <div className="mb-6 text-[#9192A0]">
          {mode === LaunchingMode.FAIR && (
            <span>
              Token allowance is based on ratio of $MAX staked in Subscription
              vault, but no more than 1%
            </span>
          )}
          {mode === LaunchingMode.IDO && (
            <span>
              IDO mode offers same price to all entries of both party & public
              rounds. Public round is{' '}
              <span className="text-[#E8E9EE]">+20%</span> more expensive.
            </span>
          )}
        </div>
        <div className="w-full rounded-lg bg-[#080a14] p-3 md:p-5 mb-6">
          <div className="flex items-center">
            {coin && (
              <img
                src={coin.metadata.image}
                alt="coinImg"
                className="w-10 h-10 rounded-full"
              />
            )}

            <span className="ml-3 text-[#E8E9EE] text-[16px] font-semibold">
              {coin.name}&nbsp;(${coin.ticker || '--'})
              <br />
              <span className="text-[12px] text-[#9192A0]">
                Contract: {coin['token'] || '--'}
              </span>
            </span>
          </div>

          <div className="w-full h-[1px] my-5 bg-[#1A1C28]"></div>

          <div className="flex flex-col w-full">
            {isIdo && (
              <span className="mt-3 flex justify-between items-center text-[#E8E9EE] text-[12px] md:text-[14px] font-medium text-wrap break-all">
                <span>Strategy config</span>
                <div
                  onClick={() => setShowCustom(true)}
                  className="text-[#E4775D] inline-flex items-center cursor-pointer hover:brightness-150"
                >
                  <EditIcon></EditIcon>
                  <span className="ml-1">Custom</span>
                </div>
              </span>
            )}
            <span className="mt-3 flex justify-between items-center text-[#9192A0] text-[12px] md:text-[14px] font-medium text-wrap break-all">
              <span>Bonding size</span>
              <span className="text-[#E8E9EE] text-right">
                {curve || coin.metadata.curveLimit || '--'} SOL
              </span>
            </span>
            <span className="mt-3 flex justify-between items-center text-[#9192A0] text-[12px] md:text-[14px] font-medium text-wrap break-all">
              <span>Party time</span>
              <span className="text-[#E8E9EE] text-right">
                {partyTime || coin.metadata.whitelistRoundPeriod || '--'}{' '}
                hour(s)
              </span>
            </span>
            {isIdo && (
              <span className="mt-3 flex justify-between items-center text-[#9192A0] text-[12px] md:text-[14px] font-medium text-wrap break-all">
                <span>Fund Expiration</span>
                <span className="text-[#E8E9EE] text-right">
                  {FUND_EXPIRATION_LIST.find(
                    (e) =>
                      e.value ===
                      (fundExp.value || coin.metadata.fundsExpiration),
                  )?.label || '--'}{' '}
                </span>
              </span>
            )}
            <span className="mt-3 flex justify-between items-center text-[#9192A0] text-[12px] md:text-[14px] font-medium text-wrap break-all">
              <span>Party Allowance Amount</span>
              <span className="text-[#E8E9EE] text-right">
                {partyLimitAmount || coin.metadata?.partyLimit || '--'} %
              </span>
            </span>
          </div>
        </div>

        <button
          disabled={!wallet.publicKey}
          onClick={() => {
            onLaunch();
          }}
          className="disabled:opacity-75 disabled:cursor-not-allowed uppercase p-1 rounded border-[2px] border-solid border-[rgba(255,255,255,0.25)] cursor-pointer hover:border-[rgba(255,255,255)] transition-all ease-in duration-150"
        >
          <div className="uppercase rounded bg-white px-6 py-2 text-[#080A14] flex items-center justify-center w-full">
            Launch now
          </div>
        </button>

        <div className="mt-6 text-[#9192A0]">
          {mode === LaunchingMode.IDO && (
            <span>
              You must set up the DCA strategy configuration later on the
              token's detail page when the total subscription vault is smaller
              than <span className="text-[#E8E9EE]">5M</span>&nbsp;$MAX
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default SetupInvestmentStrategy;
